import {getRateAndLosFromQuoteMethod} from '@finpay-development/shared-components';
import {
    ConfigRiskThreshold,
    RiskThresholdSymbols,
} from '../../admin-configuration/models/risk-threshold';
import {
    FinPayRiskClass,
    ConfigRiskClassSetting,
} from '../../admin-configuration/models/risk-class-setting';
import {
    ThresholdEnum,
    RiskAssessmentForm,
    FacilityRiskThreshold,
    MultiFacilityRiskThreshold,
    RiskAssessmentEstimate,
    ValueRisk,
    PfrEiv,
    PayerClaimRate,
    ClientRiskClassSettings,
    FacilityRiskClassSettings,
    MultiFacilityRiskClassSetting,
    ClientDeficiencySettings,
    PatientEpisodeMarginSettings,
    ReferralSourceRevenue,
    EditedFacilityCensus,
} from '../models/risk-assessment';
import {ImplementationFacility} from '../../implementation-specialist/components/implementation-clients/details/models/implementation-facility';
import {SelectedFacilityReferralSource} from '../../implementation-specialist/models/referral-source';
import { Vob } from '@finpay-estimation/estimation-types';
import {Utils} from '../../shared/utils';
import {ClientRiskAssessmentFields} from 'src/shared/model/client-risk-assessment-config';
import {
    CrossoverSelectionEnum,
    Estimate,
    EstimateSummary,
    FacilityLevelOfCare,
    QuoteMethod,
    FacilityRiskAssessment,
    // ValueRisk,
    RiskStatus,
    AdmitStatus,
} from '@finpay-estimation/estimation-types';
import {FormikProps} from 'formik';
import {RiskAssessmentFormikValues} from '../components/risk/risk-assessment-main';

export const emptyFacilityRiskAssessment: FacilityRiskAssessment = {
    isHreOnly: false,
    facilityId: 0,
    facilityName: "",
    facilityLabel: "",
    facilityCensus: 0,
    riskThresholds: [],
    daysToReimbursement: {
        value: 0, risk: RiskStatus.NONE
    },
    pfrRiskSettings: {
        value: 0, risk: RiskStatus.NONE
    },
    estAdjChgs: 0,
    estInsVal: 0,
    eivDeficiency: 0,
    pfr: 0,
    netPatientRevenueEst: 0,
    netPatientRevenueDeficiency: 0,
    patientEpisodeMargin: 0,
    profitabilityRisk: {
        value: 0, risk: RiskStatus.NONE
    },
    referralSourceRisk: {
        value: 0, risk: RiskStatus.NONE
    },
    totalVariableCost: 0,
    variableCostCoveragePercent: 0,
    minimumDownPayment: 0,
    admitStatus: AdmitStatus.NOADMIT,
    payorRiskId: 0,
    timingRiskId: 0,
    riskClassId: 1
};


class RiskAssessmentUtils {
    mergeConfigFacilityRiskThresholds = (
        configRiskThresholds: ConfigRiskThreshold[],
        facilityRiskThresholds: FacilityRiskThreshold[],
        facilityId: number
    ): FacilityRiskThreshold[] => {
        let mergedRiskThresholds: FacilityRiskThreshold[] = [];
        configRiskThresholds?.forEach(
            (configThreshold: ConfigRiskThreshold) => {
                let thisFacilityThreshold = facilityRiskThresholds?.find(
                    el =>
                        el.cfgRiskThresholdId ===
                        configThreshold.riskThresholdId
                );
                let thisMergedThreshold: FacilityRiskThreshold = {
                    facilityRiskThresholdId: null,
                    clientFacilityId: facilityId,
                    cfgRiskThresholdId: null,
                    cfgRiskThresholdCode: null,
                    lowRisk: null,
                    mediumRisk: null,
                    highRisk: null,
                };
                if (thisFacilityThreshold) {
                    thisMergedThreshold.facilityRiskThresholdId =
                        thisFacilityThreshold.facilityRiskThresholdId;
                    thisMergedThreshold.cfgRiskThresholdId =
                        configThreshold.riskThresholdId;
                    thisMergedThreshold.cfgRiskThresholdCode =
                        configThreshold.riskThresholdCode;
                    thisMergedThreshold.lowRisk = thisFacilityThreshold.lowRisk
                        ? thisFacilityThreshold.lowRisk
                        : configThreshold.lowRiskDefault;
                    thisMergedThreshold.mediumRisk =
                        thisFacilityThreshold.mediumRisk
                            ? thisFacilityThreshold.mediumRisk
                            : configThreshold.mediumRiskDefault;
                    thisMergedThreshold.highRisk =
                        thisFacilityThreshold.highRisk
                            ? thisFacilityThreshold.highRisk
                            : configThreshold.highRiskDefault;
                } else {
                    thisMergedThreshold.cfgRiskThresholdId =
                        configThreshold.riskThresholdId;
                    thisMergedThreshold.cfgRiskThresholdCode =
                        configThreshold.riskThresholdCode;
                    thisMergedThreshold.lowRisk =
                        configThreshold.lowRiskDefault;
                    thisMergedThreshold.mediumRisk =
                        configThreshold.mediumRiskDefault;
                    thisMergedThreshold.highRisk =
                        configThreshold.highRiskDefault;
                }
                mergedRiskThresholds.push(thisMergedThreshold);
            }
        );
        return mergedRiskThresholds;
    };

    getRiskThresholdSymbols = (
        riskThresholdCode: string
    ): RiskThresholdSymbols => {
        let fieldAdornment = '';
        let lowRiskSymbol = '';
        let mediumRiskSymbol = '';
        let highRiskSymbol = '';
        let position = 0;
        switch (riskThresholdCode.replace(/-|\s/g, '')) {
            case ThresholdEnum.PFRCONCRISK:
                fieldAdornment = '%';
                lowRiskSymbol = '<=';
                mediumRiskSymbol = '<';
                highRiskSymbol = '>=';
                position = 1;
                break;
            case ThresholdEnum.PROFITABILITYRISK:
                fieldAdornment = '%';
                lowRiskSymbol = '>';
                mediumRiskSymbol = '<=';
                highRiskSymbol = '<';
                position = 1;
                break;
            case ThresholdEnum.INPATREFSRCPROFITRISK:
            case ThresholdEnum.OUTPATREFSRCPROFITRISK:
                lowRiskSymbol = '>';
                mediumRiskSymbol = '<=';
                highRiskSymbol = '<';
                fieldAdornment = '$';
                position = 0;
                break;
            case ThresholdEnum.AMARISK:
                fieldAdornment = ' Days';
                lowRiskSymbol = '<=';
                mediumRiskSymbol = '<=';
                highRiskSymbol = '>';
                position = 1;
                break;
            default:
            // nothing
        }
        return {
            fieldAdornment: fieldAdornment,
            lowRiskSymbol: lowRiskSymbol,
            mediumRiskSymbol: mediumRiskSymbol,
            highRiskSymbol: highRiskSymbol,
            position: position,
        };
    };

    calculateMinimumDownPayment = (
        totalVariableCost: number,
        netPatientRevenue: number,
        remainingOopMax: number,
        totalPFR: number,
        variableCostCoveragePercent: number
    ) => {
        let minimumDownPayment = Math.max(
            totalVariableCost - netPatientRevenue,
            0
        );
        minimumDownPayment = Math.min(
            minimumDownPayment,
            remainingOopMax,
            totalPFR
        );
        return variableCostCoveragePercent < 100 ? minimumDownPayment : 0;
    };

    determineAdmitStatus = (
        estInsVal: number,
        netPatientRevenueEst: number,
        totalVariableCost: number,
        pfr: number
    ): AdmitStatus => {
        switch (true) {
            case estInsVal > totalVariableCost:
                return AdmitStatus.FAST;

            case netPatientRevenueEst > totalVariableCost:
                return AdmitStatus.ADMIT;

            case netPatientRevenueEst < totalVariableCost &&
                pfr + estInsVal >= totalVariableCost:
                return AdmitStatus.PENDING;

            case pfr + estInsVal < totalVariableCost:
                return AdmitStatus.NOADMIT;

            default:
                return AdmitStatus.NOADMIT;
        }
    };

    calculateVariableCost = (
        monthlyOperatingCost: number,
        bedCapacity: number,
        monthlyVariableCostPercent: number,
        lengthOfStay: number,
        netPatientRevenue: number
    ) => {
        const totalMonthlyVariableCost =
            monthlyOperatingCost * (monthlyVariableCostPercent / 100);

        const monthlyVariableCostPerBedPerDay =
            totalMonthlyVariableCost / bedCapacity / 30;

        const totalVarialbeCost =
            monthlyVariableCostPerBedPerDay * lengthOfStay;

        const variableCostCoveragePercent =
            (netPatientRevenue / totalVarialbeCost) * 100;

        return {
            totalMonthlyVariableCost,
            monthlyVariableCostPerBedPerDay,
            variableCostCoveragePercent,
            totalVarialbeCost,
        };
    };

    calculateFacilityRiskAssessmentsWithEstimate = (
        facility: ImplementationFacility,
        riskAssessmentFormik: FormikProps<RiskAssessmentFormikValues>,
        originalVob: Vob,
        originalEstimate: Estimate,
        multiFacilityRiskThresholds: MultiFacilityRiskThreshold[],
        facilityRiskClassSettings: FacilityRiskClassSettings[],
        clientPayerClaimRates: PayerClaimRate[],
        clientRiskAssessmentConfigs: ClientRiskAssessmentFields[],
        multiFacilityReferralSourceRevenue: ReferralSourceRevenue[]
    ): FacilityRiskAssessment[] => {
        // Use the facility ID from the VOB instead of the facility parameter
        const facilityId = originalVob?.facility?.facilityId;
        const facilityName = originalVob?.facility?.facilityName || '';
        
        // Check if facility ID is defined
        if (!facilityId) {
            console.error('Facility ID is undefined in VOB');
            // Return empty risk assessments if no facility ID
            return [
                Utils.deepClone(emptyFacilityRiskAssessment),
                Utils.deepClone(emptyFacilityRiskAssessment)
            ];
        }

        const facilityRiskAssessment: FacilityRiskAssessment =
            Utils.deepClone(emptyFacilityRiskAssessment);
        const facilityRiskAssessmentHRE: FacilityRiskAssessment =
            Utils.deepClone(emptyFacilityRiskAssessment);

        const {summary} = originalEstimate

        const {facilityCensus, riskClassId} = riskAssessmentFormik.values;

        let facilityRiskAssessmentFields:
            | ClientRiskAssessmentFields
            | undefined = clientRiskAssessmentConfigs.find(
            (riskConfig: ClientRiskAssessmentFields) => {
                // Check if riskConfig and clientFacilityId are defined
                if (!riskConfig || riskConfig.clientFacilityId === undefined) {
                    console.error("!riskConfig || riskConfig.clientFacilityId === undefined");
                }
                return riskConfig.clientFacilityId === facilityId;
            }
        );

        if (!facilityRiskAssessmentFields) {
            facilityRiskAssessmentFields = clientRiskAssessmentConfigs?.find(
                (riskConfig: ClientRiskAssessmentFields) => {
                    return riskConfig.clientFacilityId === null;
                }
            )!;
            
            // If we still can't find a valid configuration, return empty risk assessments
            if (!facilityRiskAssessmentFields) {
                console.error('No valid facility risk assessment configuration found');
            }
        }

        const {
            variableCostPct,
            isFacilityCost,
            bedCapacityIp,
            bedCapacityOp,
            monthlyOperatingCostIp,
            monthlyOperatingCostOp,
        } = facilityRiskAssessmentFields;

        let facilityThresholds = multiFacilityRiskThresholds.find(
            facthresh => facthresh.clientFacilityId === facilityId
        );

        facilityRiskAssessment.facilityId = facilityId;
        facilityRiskAssessmentHRE.facilityId = facilityId;

        facilityRiskAssessment.facilityName = facilityName;
        facilityRiskAssessmentHRE.facilityName = facilityName;

        facilityRiskAssessment.isHreOnly = false;
        facilityRiskAssessmentHRE.isHreOnly = true;

        // @ts-ignore
        facilityRiskAssessment.riskThresholds =
            facilityThresholds?.facilityRiskThresholds;
        // @ts-ignore
        facilityRiskAssessmentHRE.riskThresholds =
            facilityThresholds?.facilityRiskThresholds;

        facilityRiskAssessment.facilityLabel = 'Selected Facility';
        facilityRiskAssessmentHRE.facilityLabel = 'HRE Selected Facility';

        const totalLengthOfStay = summary!.lengthOfStay;
        const coveredLengthOfStay = summary!.lengthOfStayCovered || totalLengthOfStay;

        const estAdjChgs = summary!.totalAdjustedCharges; 

        facilityRiskAssessment.estAdjChgs = estAdjChgs 
        facilityRiskAssessmentHRE.estAdjChgs = estAdjChgs 


        const pfrEivValues: PfrEiv = this.calcPfrEiv(originalEstimate);

        facilityRiskAssessment.pfr = pfrEivValues.pfr;
        facilityRiskAssessmentHRE.pfr = pfrEivValues.pfr;

        facilityRiskAssessment.estInsVal = pfrEivValues.eiv;
        facilityRiskAssessmentHRE.estInsVal = pfrEivValues.eiv;

        const daysToReimbursement = this.calcReimbursementDays(
            originalEstimate,
            summary!.remainingDeductibleFromVob,
            facilityThresholds?.facilityRiskThresholds!
        );

        facilityRiskAssessment.daysToReimbursement = daysToReimbursement;
        facilityRiskAssessmentHRE.daysToReimbursement = daysToReimbursement;

        const pfrRiskSettings = this.calcPfrRisk(
            summary!,
            facilityThresholds?.facilityRiskThresholds!
        );

        facilityRiskAssessment.pfrRiskSettings = pfrRiskSettings;
        facilityRiskAssessmentHRE.pfrRiskSettings = pfrRiskSettings;

        facilityRiskAssessment.facilityCensus = facilityCensus!;
        facilityRiskAssessmentHRE.facilityCensus = facilityCensus!;

        const netPatientRevenueEst = this.calcNetPatientRevenueEstimate(
            facilityId,
            originalVob.payor.payorId!,
            originalVob.plan.payorPlanId!,
            riskClassId,
            pfrEivValues.pfr,
            pfrEivValues.eiv,
            true,
            facilityRiskClassSettings,
            clientPayerClaimRates
        );

        const netPatientRevenueEstHRE = this.calcNetPatientRevenueEstimate(
            originalVob.facility.facilityId,
            originalVob.payor.payorId!,
            originalVob.plan.payorPlanId!,
            riskClassId,
            pfrEivValues.pfr,
            pfrEivValues.eiv,
            false,
            facilityRiskClassSettings,
            clientPayerClaimRates
        );

        facilityRiskAssessment.netPatientRevenueEst = netPatientRevenueEst;
        facilityRiskAssessmentHRE.netPatientRevenueEst =
            netPatientRevenueEstHRE;

        // TODO: similar to comment below
        // TODO: figure out a more saphisticated way of determining isInPatient,
        // given the different loc in different vob
        // bedCapacityIp or bedCapacityOp
        const variableCostCalcResult = this.calculateVariableCost(
            monthlyOperatingCostIp!,
            bedCapacityIp!,
            variableCostPct!,
            totalLengthOfStay,
            facilityRiskAssessment.netPatientRevenueEst
        );

		const variableCostCalcResultHre = this.calculateVariableCost(
            monthlyOperatingCostIp!,
            bedCapacityIp!,
            variableCostPct!,
            totalLengthOfStay,
            facilityRiskAssessmentHRE.netPatientRevenueEst
        );

        let estimatedAdjustedCost: number = 0;

        if (isFacilityCost) {
            estimatedAdjustedCost = this.calcOperatingCostsByFacility(
                monthlyOperatingCostIp!,
                bedCapacityIp!,
                facilityCensus!,
                variableCostPct!,
                coveredLengthOfStay
            );
        } else {
            estimatedAdjustedCost = this.calcOperatingCostsByLevelOfCare(
                facilityRiskAssessment.facilityCensus,
                originalEstimate,
                variableCostPct
            );
        }

        const adjustedGrossMinusNPRestimate: number =
            facilityRiskAssessment.estAdjChgs -
            facilityRiskAssessment.netPatientRevenueEst;
        
        const adjustedGrossMinusNPRestimateHre: number =
            facilityRiskAssessmentHRE.estAdjChgs -
            facilityRiskAssessmentHRE.netPatientRevenueEst;

        const calculatedAdmissionCost =
            adjustedGrossMinusNPRestimate + estimatedAdjustedCost;
        
        const calculatedAdmissionCostHre =
            adjustedGrossMinusNPRestimateHre + estimatedAdjustedCost;

        const estimatedAdmissionProfit: number =
            Math.max(facilityRiskAssessment.estAdjChgs - calculatedAdmissionCost, 0);
        
        const estimatedAdmissionProfitHre: number =
            Math.max(facilityRiskAssessmentHRE.estAdjChgs - calculatedAdmissionCostHre, 0);

        // patient episode margin
        const patientEpisodeMargin =
            (estimatedAdmissionProfit / facilityRiskAssessment.estAdjChgs) *
            100;

        const patientEpisodeMarginHre =
            (estimatedAdmissionProfitHre / facilityRiskAssessmentHRE.estAdjChgs) *
            100;

        facilityRiskAssessment.patientEpisodeMargin = patientEpisodeMargin;
        facilityRiskAssessmentHRE.patientEpisodeMargin = patientEpisodeMarginHre;

        const profitabilityRisk = this.calcProfitRisk(
            facilityRiskAssessment.patientEpisodeMargin,
            facilityThresholds?.facilityRiskThresholds!
        );
        
        const profitabilityRiskHre = this.calcProfitRisk(
            facilityRiskAssessmentHRE.patientEpisodeMargin,
            facilityThresholds?.facilityRiskThresholds!
        );

        facilityRiskAssessment.profitabilityRisk = profitabilityRisk;
        facilityRiskAssessmentHRE.profitabilityRisk = profitabilityRiskHre;

        facilityRiskAssessment.totalVariableCost =
            variableCostCalcResult.totalVarialbeCost;
        facilityRiskAssessmentHRE.totalVariableCost =
            variableCostCalcResultHre.totalVarialbeCost;

        facilityRiskAssessment.variableCostCoveragePercent =
            variableCostCalcResult.variableCostCoveragePercent;
        facilityRiskAssessmentHRE.variableCostCoveragePercent =
            variableCostCalcResultHre.variableCostCoveragePercent;

        const minimumDownPayment = this.calculateMinimumDownPayment(
            facilityRiskAssessment.totalVariableCost,
            facilityRiskAssessment.netPatientRevenueEst,
            summary!.remainingOopMaxAfterServices,
            summary!.totalPFR,
            facilityRiskAssessment.variableCostCoveragePercent
        );

		const minimumDownPaymentHre = this.calculateMinimumDownPayment(
            facilityRiskAssessment.totalVariableCost,
            facilityRiskAssessment.netPatientRevenueEst,
            summary!.remainingOopMaxAfterServices,
            summary!.totalPFR,
            facilityRiskAssessmentHRE.variableCostCoveragePercent
        );

        facilityRiskAssessment.minimumDownPayment = minimumDownPayment;
        facilityRiskAssessmentHRE.minimumDownPayment = minimumDownPaymentHre;

        const minimumRevenueAmt: number =
            this.calcMinimumRevenueAmts(originalEstimate);
        // eiv/pfr deficiencies

        const eivDeficiency =
            minimumRevenueAmt > pfrEivValues.eiv
                ? minimumRevenueAmt - pfrEivValues.eiv
                : 0;

        facilityRiskAssessment.eivDeficiency = eivDeficiency;
        facilityRiskAssessmentHRE.eivDeficiency = eivDeficiency;

        const netPatientRevenueDeficiency =
            minimumRevenueAmt > facilityRiskAssessment.netPatientRevenueEst
                ? minimumRevenueAmt -
                  facilityRiskAssessment.netPatientRevenueEst
                : 0;

        const netPatientRevenueDeficiencyHre =
            minimumRevenueAmt > facilityRiskAssessmentHRE.netPatientRevenueEst
                ? minimumRevenueAmt -
                  facilityRiskAssessmentHRE.netPatientRevenueEst
                    : 0;

        facilityRiskAssessment.netPatientRevenueDeficiency =
            netPatientRevenueDeficiency;
        facilityRiskAssessmentHRE.netPatientRevenueDeficiency =
            netPatientRevenueDeficiencyHre;

        const referralSourceRisk = this.calcReferralSourceRevenueEstimate(
            facility,
            riskAssessmentFormik,
            originalEstimate,
            multiFacilityReferralSourceRevenue,
            facilityThresholds?.facilityRiskThresholds!
        );
        facilityRiskAssessment.referralSourceRisk = referralSourceRisk;
        facilityRiskAssessmentHRE.referralSourceRisk = referralSourceRisk;

        const admitStatus = this.determineAdmitStatus(
            facilityRiskAssessment.estInsVal,
            facilityRiskAssessment.netPatientRevenueEst,
            facilityRiskAssessment.totalVariableCost,
            facilityRiskAssessment.pfr
        );

        facilityRiskAssessment.admitStatus = admitStatus;
        facilityRiskAssessmentHRE.admitStatus = admitStatus;

        return [facilityRiskAssessment, facilityRiskAssessmentHRE];
    };

    // makeFacilityEstimate = (
    //     facility: ImplementationFacility,
    //     originalVob: Vob,
    //     originalEstimate: Estimate,
    //     clientLevelsOfCare: FacilityLevelOfCare[]
    // ): RiskAssessmentEstimate => {
    //     let facilityEstimateCopy: Estimate =
    //         Utils.deepClone(originalEstimate);
    //     // it's possible we can't make a valid estimate using this Facility
    //     let isValid: boolean = true;
    //     // this array will hold the equivalent selectedLevelsOfCare for this facilityId
    //     let matchingLevelsOfCare: EditEstLevelOfCare[] = [];
    //     // facilityEstimateCopy.facility.facilityId = facility.facilityId;
    //     // facilityEstimateCopy.facility.facilityName = facility.facilityName;
    //     // if selectedLevelsOfCareFacilityType = 'multi' we just keep levels of care
    //     // otherwise we have to find matching ones for this facility
    //     if (
    //         facilityEstimateCopy.selectedLevelsOfCareFacilityType === 'selected'
    //     ) {
    //         // find levels of care in this facility that match those in originalEstimate
    //         facilityEstimateCopy.selectedLevelsOfCare?.forEach(loc => {
    //             let matchingFacilityLevelofCare = clientLevelsOfCare.find(
    //                 item =>
    //                     item.clientFacilityId === facility.facilityId &&
    //                     item.cfgLevelOfCareId === loc.cfgLevelOfCareId
    //             );
    //             if (matchingFacilityLevelofCare) {
    //                 let locCopy: EditEstLevelOfCare = Utils.deepClone(
    //                     matchingFacilityLevelofCare
    //                 );
    //                 // is this level of care in network?
    //                 let locInNetwork: boolean = false;
    //                 if (
    //                     locCopy.facilityPayorLocRateLos?.cfgPayorPlanId ===
    //                     originalVob.payer.payorId
    //                 ) {
    //                     locInNetwork = true;
    //                 }
    //                 // facility-specific level of care rates and length of stay
    //                 let ratesLos: RatesLos = getRateAndLosFromQuoteMethod(
    //                     locCopy,
    //                     originalEstimate.quoteMethod!,
    //                     locInNetwork
    //                 );
    //                 // manually entered rate and los must be brought over from originalEstimate
    //                 switch (facilityEstimateCopy.quoteMethod) {
    //                     case 'sca':
    //                         locCopy.scaRatesLos = {
    //                             pdrRate: loc.scaRatesLos?.pdrRate,
    //                             losDays: loc.scaRatesLos?.losDays,
    //                         };
    //                         locCopy.selectedRateLos = {
    //                             pdrRate: loc.scaRatesLos?.pdrRate,
    //                             losDays: loc.scaRatesLos?.losDays,
    //                         };
    //                         break;
    //                     case 'manual':
    //                         locCopy.manualRatesLos = {
    //                             pdrRate: loc.manualRatesLos?.losDays,
    //                             losDays: ratesLos.losDays,
    //                         };
    //                         locCopy.selectedRateLos = {
    //                             pdrRate: loc.manualRatesLos?.losDays,
    //                             losDays: ratesLos.losDays,
    //                         };
    //                         break;
    //                     default:
    //                         locCopy.manualRatesLos = {
    //                             pdrRate: ratesLos.pdrRate,
    //                             losDays: 0,
    //                         };
    //                         locCopy.scaRatesLos = {
    //                             pdrRate: 0,
    //                             losDays: 0,
    //                         };
    //                         locCopy.selectedRateLos = {
    //                             pdrRate: ratesLos.pdrRate,
    //                             losDays: ratesLos.losDays,
    //                         };
    //                 }
    //                 if (originalVob.selfPay) {
    //                     if (locCopy?.facilityPayorLocRateLos?.isCovered) {
    //                         locCopy.facilityPayorLocRateLos.isCovered = false;
    //                     }
    //                 }
    //                 matchingLevelsOfCare.push(locCopy);
    //             } else {
    //                 isValid = false;
    //             }
    //         });
    //
    //         // add selectedLevelsOfCare for this facility to facilityEstimateCopy
    //         if (isValid && matchingLevelsOfCare.length > 0) {
    //             facilityEstimateCopy.selectedLevelsOfCare =
    //                 matchingLevelsOfCare;
    //         }
    //     }
    //
    //     return {
    //         isValid: isValid,
    //         estimateBody: facilityEstimateCopy,
    //     };
    // };

    calcReimbursementDays = (
        estimateBody: Estimate,
        deductible: number,
        riskThresholds: FacilityRiskThreshold[]
    ): ValueRisk => {
        let reimburseData: ValueRisk = {
            value: 0,
            risk: RiskStatus.NONE,
        };
        let daysTilDeductibleSpent: number;
        let riskStatus: RiskStatus = RiskStatus.NONE;

        // get first level of care (try sortOrder but might not be set)
        let firstLevelOfCare: FacilityLevelOfCare | null | undefined = null;
        let sortableLoc = estimateBody.facilityLevelOfCare?.filter(
            loc => loc.sortOrder && loc.locType === 0
        );
        if (sortableLoc) {
            sortableLoc.sort((a, b) => {
                return a.sortOrder! - b.sortOrder!;
            });
            firstLevelOfCare = sortableLoc.length ? sortableLoc[0] : null;
        } else {
            // no sort order has been applied.  Look for a level of care with "detox" in the name
            const detoxLocs = estimateBody.facilityLevelOfCare?.filter(loc => {
                return (
                    loc.facilityLevelOfCareName
                        ?.toString()
                        .toLowerCase()
                        .includes('detox') ||
                    loc?.facilityLevelOfCareCode
                        ?.toString()
                        .toLowerCase()
                        .includes('detox')
                );
            });
            if (detoxLocs) {
                firstLevelOfCare = detoxLocs.length ? detoxLocs[0] : null;
            }
        }
        if (firstLevelOfCare) {
            daysTilDeductibleSpent = deductible / firstLevelOfCare.pdrRate!;
        } else {
            // can't determine firstLevelOfCare from sort order or "detox", so just use selectedLevelsOfCare[0]
            firstLevelOfCare = estimateBody.facilityLevelOfCare![0];
            daysTilDeductibleSpent = deductible / firstLevelOfCare.pdrRate!;
        }

        // get AMARISK threshold
        let amaRiskThresholds = riskThresholds?.find(
            thresh => thresh.cfgRiskThresholdCode === ThresholdEnum.AMARISK
        );
        if (amaRiskThresholds) {
            switch (true) {
                case daysTilDeductibleSpent <= amaRiskThresholds.lowRisk!:
                    riskStatus = RiskStatus.LOW;
                    break;
                case daysTilDeductibleSpent > amaRiskThresholds.lowRisk! &&
                    daysTilDeductibleSpent < amaRiskThresholds.highRisk!:
                    riskStatus = RiskStatus.MEDIUM;
                    break;
                case daysTilDeductibleSpent >= amaRiskThresholds.highRisk!:
                    riskStatus = RiskStatus.HIGH;
                    break;
            }
        }

        reimburseData.value = daysTilDeductibleSpent;
        reimburseData.risk = riskStatus;
        return reimburseData;
    };

    calcPfrRisk = (
        pfrSummary: EstimateSummary,
        riskThresholds: FacilityRiskThreshold[]
    ): ValueRisk => {
        let pfrRiskData: ValueRisk = {
            value: 0,
            risk: RiskStatus.NONE,
        };
        let totalChgs: number = pfrSummary.totalAdjustedCharges;
        let pfrPct = (pfrSummary.totalPFR / totalChgs) * 100;
        let pfrRisk = RiskStatus.NONE;
        // get PFRCONCRISK threshold
        let pfrConcRiskThresholds = riskThresholds?.find(
            thresh => thresh.cfgRiskThresholdCode === ThresholdEnum.PFRCONCRISK
        );
        if (pfrConcRiskThresholds) {
            switch (true) {
                case pfrPct <= pfrConcRiskThresholds.lowRisk!:
                    pfrRisk = RiskStatus.LOW;
                    break;
                case pfrPct > pfrConcRiskThresholds.lowRisk! &&
                    pfrPct < pfrConcRiskThresholds.mediumRisk!:
                    pfrRisk = RiskStatus.MEDIUM;
                    break;
                case pfrPct >= pfrConcRiskThresholds.mediumRisk!:
                    pfrRisk = RiskStatus.HIGH;
                    break;
            }
        }
        pfrRiskData.value = pfrPct;
        pfrRiskData.risk = pfrRisk;
        return pfrRiskData;
    };

    calcPfrEiv = (estimate: Estimate): PfrEiv => {
        const eiv = Math.max(
            estimate.summary!.coveredCharges - estimate.summary!.netPFR,
            0
        ) ?? 0.0;

        const thisPfrEiv: PfrEiv = {
            pfr: estimate.summary!.totalPFR ?? 0.0,
            eiv
        };

        return thisPfrEiv;
    };

    calcNetPatientRevenueEstimate = (
        facilityId: number,
        payerId: number,
        planId: number,
        selectedRiskClassId: number | undefined,
        pfr: number,
        eiv: number,
        isCleared: boolean,
        facilityRiskClassSettings: FacilityRiskClassSettings[],
        clientPayerClaimRates: PayerClaimRate[]
    ): number => {
        // first get claim rate
        let claimRate: number = 0;

        let claim = clientPayerClaimRates?.find(claimRate => {
            return (
                (claimRate.cfgPayorId === payerId &&
                    claimRate.cfgPayorPlanId === null) ||
                (claimRate.cfgPayorId === null &&
                    claimRate.cfgPayorPlanId === planId)
            );
        });

        if (claim) {
            claimRate = claim.claimRatePct || 0;
        }
        // second get facilityRiskClassSettings
        let clearedValue: number = 0;
        let hreValue: number = 0;

        let selectedFacilityRiskClass = facilityRiskClassSettings.find(
            facrisk =>
                facrisk.clientFacilityId === facilityId &&
                facrisk.riskClassId === selectedRiskClassId
        );

        if (selectedFacilityRiskClass) {
            clearedValue = selectedFacilityRiskClass.riskClassCleared;
            hreValue = selectedFacilityRiskClass.riskClassHre;
        }
        // calculate
        let payerPays: number = claimRate > 0 ? eiv * (claimRate / 100) : 0;

        let patientPays: number = 0;

        if (isCleared) {
            patientPays = clearedValue > 0 ? pfr * (clearedValue / 100) : 0;
        } else {
            patientPays = hreValue > 0 ? pfr * (hreValue / 100) : 0;
        }

        return payerPays + patientPays;
    };

    calcOperatingCostsByLevelOfCare = (
        facilityCensus: number,
        estimateBody: Estimate,
        clientVariableCostPct: number | null | undefined
    ): number => {
        // Per diem Operating Cost method
        let facilityEstimateCopy: Estimate = Utils.deepClone(estimateBody);
        let totalLocCosts: number = 0;
        let censusAdjustedCostPerBed: number = 0;

        facilityEstimateCopy.facilityLevelOfCare?.forEach(loc => {
            censusAdjustedCostPerBed = 0;
            if (loc.perDiemOperatingCost) {
                censusAdjustedCostPerBed =
                    loc.perDiemOperatingCost / (facilityCensus / 100);
            }
            if (clientVariableCostPct) {
                censusAdjustedCostPerBed =
                    censusAdjustedCostPerBed * (clientVariableCostPct / 100);
            }
            totalLocCosts += censusAdjustedCostPerBed * loc.losDays!;
        });

        return totalLocCosts;
    };

    calcOperatingCostsByFacility = (
        monthlyOperatingCost: number,
        bedCapacity: number,
        facilityCensus: number,
        clientVariableCostPct: number,
        lengthOfStay: number
    ): number => {
        const totalVariableCost =
            monthlyOperatingCost * (clientVariableCostPct / 100);
        const totalFixedCosts = monthlyOperatingCost - totalVariableCost;

        const variableCostPerBedPerDay = totalVariableCost / bedCapacity / 30;
        const fixedCostPerBedPerDay = totalFixedCosts / bedCapacity / 30;

        const censusAdjustedFixedCostPerBedPerDay =
            fixedCostPerBedPerDay / (facilityCensus / 100);

        const variableCostForAdmission =
            variableCostPerBedPerDay * lengthOfStay;
        const fixedCostForAdmission =
            censusAdjustedFixedCostPerBedPerDay * lengthOfStay;

        const totalAdmissionCost =
            variableCostForAdmission + fixedCostForAdmission;
        return totalAdmissionCost;
    };

    calcMinimumRevenueAmts(estimateBody: Estimate): number {
        let minRevenueAmt: number = 0;

        estimateBody.facilityLevelOfCare?.forEach(loc => {
            let isFlatRate: boolean = loc.minRevenueFlatRate
                ? loc.minRevenueFlatRate
                : false;

            if (isFlatRate) {
                if (loc.minRevenueAmount) {
                    minRevenueAmt += !isNaN(loc.minRevenueAmount)
                        ? loc.minRevenueAmount
                        : 0;
                }
            } else {
                if (loc.minRevenueAmount) {
                    if (!isNaN(loc.minRevenueAmount) && !isNaN(loc.losDays!)) {
                        minRevenueAmt += loc.minRevenueAmount * loc.losDays!;
                    }
                }
            }
        });

        return minRevenueAmt;
    }

    calcProfitRisk = (
        patientEpisodeMargin: number,
        riskThresholds: FacilityRiskThreshold[]
    ): ValueRisk => {
        let profitRiskData: ValueRisk = {
            value: 0,
            risk: RiskStatus.NONE,
        };
        let profitRisk = RiskStatus.NONE;
        // get PROFITABILITYRISK threshold
        let pfrConcRiskThresholds = riskThresholds?.find(
            thresh =>
                thresh.cfgRiskThresholdCode === ThresholdEnum.PROFITABILITYRISK
        );
        if (pfrConcRiskThresholds) {
            switch (true) {
                case patientEpisodeMargin >= pfrConcRiskThresholds.lowRisk!:
                    profitRisk = RiskStatus.LOW;
                    break;
                case patientEpisodeMargin > pfrConcRiskThresholds.highRisk!:
                    profitRisk = RiskStatus.MEDIUM;
                    break;
                default:
                    profitRisk = RiskStatus.HIGH;
                    break;
            }
        }
        profitRiskData.value = patientEpisodeMargin;
        profitRiskData.risk = profitRisk;
        return profitRiskData;
    };

    calcReferralSourceRevenueEstimate = (
        facility: ImplementationFacility,
        riskAssessmentForm: FormikProps<RiskAssessmentFormikValues>,
        estimateBody: Estimate,
        multiFacilityReferralSourceRevenue: ReferralSourceRevenue[],
        riskThresholds: FacilityRiskThreshold[]
    ): ValueRisk => {
        // Revenue Estimate from referral source data = sum of quoted LOS for each LOC * rate from "referral source revenue data" table for each LOC

        let referralRevenueData: ValueRisk = {
            value: 0,
            risk: RiskStatus.NONE,
        };

        let facilityHasReferralSourceRevenue: boolean = false;

        // has a referral source been selected?
        if (riskAssessmentForm.values.facilityReferralSourceId) {
            const selectedReferralSourceId: number =
                riskAssessmentForm.values.facilityReferralSourceId;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            let facilityReferralSource = <ReferralSourceRevenue | undefined>{};
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            let selectedFacilityReferralSource = <
                ReferralSourceRevenue | undefined
            >{};

            estimateBody.facilityLevelOfCare?.forEach(loc => {
                facilityReferralSource =
                    multiFacilityReferralSourceRevenue.find(
                        refsrc =>
                            refsrc.facilityReferralSourceId ===
                                selectedReferralSourceId &&
                            refsrc.clientFacilityId === facility.facilityId &&
                            refsrc.facilityLevelOfCareId ===
                                loc.facilityLevelOfCareId
                    );

                selectedFacilityReferralSource =
                    multiFacilityReferralSourceRevenue.find(
                        refsrc =>
                            refsrc.facilityReferralSourceId ===
                            selectedReferralSourceId
                    );

                if (selectedFacilityReferralSource?.clientReferralSourceId) {
                    facilityReferralSource =
                        multiFacilityReferralSourceRevenue.find(
                            refsrc =>
                                refsrc.clientReferralSourceId ===
                                    selectedFacilityReferralSource?.clientReferralSourceId &&
                                refsrc.clientFacilityId ===
                                    facility.facilityId &&
                                refsrc.facilityLevelOfCareId ===
                                    loc.facilityLevelOfCareId
                        );
                }

                if (facilityReferralSource) {
                    facilityHasReferralSourceRevenue = true;

                    let referralRevenue =
                        facilityReferralSource.locReferralRevenue
                            ? facilityReferralSource.locReferralRevenue
                            : 0;

                    const revenueTotal: number = loc.losDays! * referralRevenue;

                    referralRevenueData.value += revenueTotal;
                }
            });
        }

        if (facilityHasReferralSourceRevenue) {
            // TODO: figure out a more saphisticated way of determining isInPatient,
            // given the different loc in different vob
            // const thresholdCode = isInpatient? ThresholdEnum.INPATREFSRCPROFITRISK : ThresholdEnum.OUTPATREFSRCPROFITRISK
            const thresholdCode = ThresholdEnum.INPATREFSRCPROFITRISK;
            let pfrConcRiskThresholds = riskThresholds?.find(
                thresh => thresh.cfgRiskThresholdCode === thresholdCode
            );
            if (pfrConcRiskThresholds) {
                switch (true) {
                    case referralRevenueData.value >
                        pfrConcRiskThresholds.lowRisk!:
                        referralRevenueData.risk = RiskStatus.LOW;
                        break;
                    case referralRevenueData.value <=
                        pfrConcRiskThresholds.mediumRisk!:
                        referralRevenueData.risk = RiskStatus.MEDIUM;
                        break;
                    case referralRevenueData.value <=
                        pfrConcRiskThresholds.highRisk!:
                        referralRevenueData.risk = RiskStatus.HIGH;
                        break;
                }
            }
        }

        return referralRevenueData;
    };

    mergeRiskClassSettings = (
        coreRiskClasses: FinPayRiskClass[],
        configRiskClassSettings: ConfigRiskClassSetting[],
        clientRiskClassSettings: ClientRiskClassSettings[],
        multiFacilityRiskClassSettings: MultiFacilityRiskClassSetting[]
    ): FacilityRiskClassSettings[] => {
        let multiFacRiskClasses: FacilityRiskClassSettings[] = [];

        // this function will coalesce facility/client/config risk settings and return non-null
        function coalesce(
            facSetting: number | null | undefined,
            clientSetting: number | null | undefined,
            configSetting: number | null | undefined
        ) {
            var len = arguments.length;
            for (var i = 0; i < len; i++) {
                if (arguments[i] !== null && arguments[i] !== undefined) {
                    return arguments[i];
                }
            }
            return null;
        }

        multiFacilityRiskClassSettings?.forEach(
            (facRiskClassSetting: MultiFacilityRiskClassSetting) => {
                coreRiskClasses?.forEach((coreRiskClass: FinPayRiskClass) => {
                    let configClearedValue: number = 0;
                    let configHreValue: number = 0;
                    let clientClearedValue: number | undefined | null = null;
                    let clientHreValue: number | undefined | null = null;
                    let facilityClearedValue: number | undefined | null = null;
                    let facilityHreValue: number | undefined | null = null;
                    // get the cleared/hre config setting
                    let configSettings = configRiskClassSettings.find(
                        configrc =>
                            configrc.fpRiskClassId === coreRiskClass.riskClassId
                    );
                    if (configSettings) {
                        configClearedValue = configSettings.riskClassCleared
                            ? configSettings.riskClassCleared
                            : 0;
                        configHreValue = configSettings.riskClassHre
                            ? configSettings.riskClassHre
                            : 0;
                    }
                    // get the cleared/hre client setting
                    let clientSettings = clientRiskClassSettings.find(
                        clientrc =>
                            clientrc.riskClassId === coreRiskClass.riskClassId
                    );
                    if (clientSettings) {
                        clientClearedValue = clientSettings.riskClassCleared
                            ? clientSettings.riskClassCleared
                            : null;
                        clientHreValue = clientSettings.riskClassHre
                            ? clientSettings.riskClassHre
                            : null;
                    }
                    // get the cleared/hre facility setting
                    let multiFacilitySettings =
                        multiFacilityRiskClassSettings.find(
                            multiFacrc =>
                                multiFacrc.clientFacilityId ===
                                facRiskClassSetting.clientFacilityId
                        );
                    if (multiFacilitySettings) {
                        let facilitySettings =
                            multiFacilitySettings.facilityRiskClassSettings.find(
                                facilityrc =>
                                    facilityrc.riskClassId ===
                                    coreRiskClass.riskClassId
                            );
                        if (facilitySettings) {
                            facilityClearedValue =
                                facilitySettings.riskClassCleared
                                    ? facilitySettings.riskClassCleared
                                    : null;
                            facilityHreValue = facilitySettings.riskClassHre
                                ? facilitySettings.riskClassHre
                                : null;
                        }
                    }

                    multiFacRiskClasses.push({
                        clientFacilityId: facRiskClassSetting.clientFacilityId,
                        riskClassId: coreRiskClass.riskClassId,
                        riskClassCleared: coalesce(
                            facilityClearedValue,
                            clientClearedValue,
                            configClearedValue
                        ),
                        riskClassHre: coalesce(
                            facilityHreValue,
                            clientHreValue,
                            configHreValue
                        ),
                    });
                });
            }
        );
        return multiFacRiskClasses;
    };

    mergeReferralSourceRevenue(
        multiFacilityReferralSources: SelectedFacilityReferralSource[],
        multiFacilityReferralSourceRevenue: ReferralSourceRevenue[]
    ): ReferralSourceRevenue[] {
        let mergedReferralSourceData = new Array<ReferralSourceRevenue>();

        multiFacilityReferralSourceRevenue?.forEach(
            (refRevenue: ReferralSourceRevenue) => {
                let refRevenueCopy: ReferralSourceRevenue =
                    Utils.deepClone(refRevenue);
                let facilityReferralSource = multiFacilityReferralSources.find(
                    facref =>
                        facref.facilityReferralSourceId ===
                        refRevenue.facilityReferralSourceId
                );
                if (facilityReferralSource?.clientReferralSourceId) {
                    refRevenueCopy.clientReferralSourceId =
                        facilityReferralSource.clientReferralSourceId;
                } else {
                    refRevenueCopy.clientReferralSourceId = null;
                }
                mergedReferralSourceData.push(refRevenueCopy);
            }
        );

        return mergedReferralSourceData;
    }

    findInvalidProperties = (
        properties: Record<string, any>,
        requiredProperties: string[],
        validator: (value: any) => boolean
    ): [string[], boolean] => {
        const listOfMissingOrInvalidProperties: string[] =
            requiredProperties.filter(prop => {
                const value = properties[prop];
                return !validator(value);
            });

        if (listOfMissingOrInvalidProperties.length > 0) {
            return [listOfMissingOrInvalidProperties, true];
        } else {
            return [listOfMissingOrInvalidProperties, false];
        }
    };

    generateMessage = (
        missingProperties: string[],
        prettyPropertyMapping: Record<string, string>,
        messageStart: string = ''
    ) => {
        missingProperties.forEach((prop: string, index: number) => {
            const string = prettyPropertyMapping[prop];

            if (string) {
                messageStart += `${index === 0 ? '' : ','} ${string}`;
            }
        });

        return messageStart;
    };

    isValidNumber = (value: number | undefined | null) => {
        if (value === null || value === undefined) return false;

        if (typeof value === 'string') value = Number(value);

        if (isNaN(value)) return false;

        if (value < 0) return false;

        return true;
    };
}

export const riskAssessmentUtils = new RiskAssessmentUtils();
